/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { SiteContainerPaging, SiteEntry, SiteGroupEntry, SiteGroupPaging, SiteMemberEntry, SiteMemberPaging, SiteMembershipRequestEntry, SiteMembershipRequestPaging, SiteMembershipRequestWithPersonPaging, SitePaging, SiteRoleEntry, SiteRolePaging } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class SitesApi extends BaseApi {
    approveSiteMembershipRequest(siteId, inviteeId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(inviteeId, 'inviteeId');
        const postBody = opts === null || opts === void 0 ? void 0 : opts.siteMembershipApprovalBody;
        const pathParams = {
            siteId,
            inviteeId
        };
        return this.post({
            path: '/sites/{siteId}/site-membership-requests/{inviteeId}/approve',
            pathParams,
            bodyParam: postBody
        });
    }
    createSite(siteBodyCreate, opts) {
        throwIfNotDefined(siteBodyCreate, 'siteBodyCreate');
        opts = opts || {};
        const queryParams = {
            skipConfiguration: opts === null || opts === void 0 ? void 0 : opts.skipConfiguration,
            skipAddToFavorites: opts === null || opts === void 0 ? void 0 : opts.skipAddToFavorites,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/sites',
            queryParams,
            bodyParam: siteBodyCreate,
            returnType: SiteEntry
        });
    }
    createSiteMembership(siteId, siteMembershipBodyCreate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(siteMembershipBodyCreate, 'siteMembershipBodyCreate');
        const pathParams = {
            siteId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/sites/{siteId}/members',
            pathParams,
            queryParams,
            bodyParam: siteMembershipBodyCreate,
            returnType: SiteMemberEntry
        });
    }
    createSiteMembershipRequestForPerson(personId, siteMembershipRequestBodyCreate, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteMembershipRequestBodyCreate, 'siteMembershipRequestBodyCreate');
        const pathParams = {
            personId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/people/{personId}/site-membership-requests',
            pathParams,
            queryParams,
            bodyParam: siteMembershipRequestBodyCreate,
            returnType: SiteMembershipRequestEntry
        });
    }
    deleteSite(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            siteId
        };
        const queryParams = {
            permanent: opts === null || opts === void 0 ? void 0 : opts.permanent
        };
        return this.delete({
            path: '/sites/{siteId}',
            pathParams,
            queryParams
        });
    }
    deleteSiteMembership(siteId, personId) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(personId, 'personId');
        const pathParams = {
            siteId,
            personId
        };
        return this.delete({
            path: '/sites/{siteId}/members/{personId}',
            pathParams
        });
    }
    deleteSiteMembershipForPerson(personId, siteId) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            personId,
            siteId
        };
        return this.delete({
            path: '/people/{personId}/sites/{siteId}',
            pathParams
        });
    }
    deleteSiteMembershipRequestForPerson(personId, siteId) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            personId,
            siteId
        };
        return this.delete({
            path: '/people/{personId}/site-membership-requests/{siteId}',
            pathParams
        });
    }
    getSite(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            siteId
        };
        const queryParams = {
            relations: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.relations, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/sites/{siteId}',
            pathParams,
            queryParams,
            returnType: SiteEntry
        });
    }
    getSiteContainer(siteId, containerId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(containerId, 'containerId');
        const pathParams = {
            siteId,
            containerId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/sites/{siteId}/containers/{containerId}',
            pathParams,
            queryParams
        });
    }
    getSiteMembership(siteId, personId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(personId, 'personId');
        const pathParams = {
            siteId,
            personId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/sites/{siteId}/members/{personId}',
            pathParams,
            queryParams,
            returnType: SiteMemberEntry
        });
    }
    getSiteMembershipForPerson(personId, siteId) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            personId,
            siteId
        };
        return this.get({
            path: '/people/{personId}/sites/{siteId}',
            pathParams,
            returnType: SiteRoleEntry
        });
    }
    getSiteMembershipRequestForPerson(personId, siteId, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            personId,
            siteId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people/{personId}/site-membership-requests/{siteId}',
            pathParams,
            queryParams,
            returnType: SiteMembershipRequestEntry
        });
    }
    getSiteMembershipRequests(opts) {
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/site-membership-requests',
            queryParams,
            returnType: SiteMembershipRequestWithPersonPaging
        });
    }
    listSiteContainers(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            siteId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/sites/{siteId}/containers',
            pathParams,
            queryParams,
            returnType: SiteContainerPaging
        });
    }
    listSiteMembershipRequestsForPerson(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        const pathParams = {
            personId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/people/{personId}/site-membership-requests',
            pathParams,
            queryParams,
            returnType: SiteMembershipRequestPaging
        });
    }
    listSiteMemberships(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        opts = opts || {};
        const pathParams = {
            siteId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/sites/{siteId}/members',
            pathParams,
            queryParams,
            returnType: SiteMemberPaging
        });
    }
    listSiteMembershipsForPerson(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        opts = opts || {};
        const pathParams = {
            personId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            relations: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.relations, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where
        };
        return this.get({
            path: '/people/{personId}/sites',
            pathParams,
            queryParams,
            returnType: SiteRolePaging
        });
    }
    listSites(opts) {
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            relations: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.relations, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where
        };
        return this.get({
            path: '/sites',
            queryParams,
            returnType: SitePaging
        });
    }
    rejectSiteMembershipRequest(siteId, inviteeId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(inviteeId, 'inviteeId');
        const postBody = opts === null || opts === void 0 ? void 0 : opts.siteMembershipRejectionBody;
        const pathParams = {
            siteId,
            inviteeId
        };
        return this.post({
            path: '/sites/{siteId}/site-membership-requests/{inviteeId}/reject',
            pathParams,
            bodyParam: postBody
        });
    }
    updateSite(siteId, siteBodyUpdate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(siteBodyUpdate, 'siteBodyUpdate');
        opts = opts || {};
        const pathParams = {
            siteId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/sites/{siteId}',
            pathParams,
            queryParams,
            bodyParam: siteBodyUpdate,
            returnType: SiteEntry
        });
    }
    updateSiteMembership(siteId, personId, siteMembershipBodyUpdate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteMembershipBodyUpdate, 'siteMembershipBodyUpdate');
        const pathParams = {
            siteId,
            personId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/sites/{siteId}/members/{personId}',
            pathParams,
            queryParams,
            bodyParam: siteMembershipBodyUpdate,
            returnType: SiteMemberEntry
        });
    }
    updateSiteMembershipRequestForPerson(personId, siteId, siteMembershipRequestBodyUpdate, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(siteMembershipRequestBodyUpdate, 'siteMembershipRequestBodyUpdate');
        const pathParams = {
            personId,
            siteId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/people/{personId}/site-membership-requests/{siteId}',
            pathParams,
            queryParams,
            bodyParam: siteMembershipRequestBodyUpdate,
            returnType: SiteMembershipRequestEntry
        });
    }
    createSiteGroupMembership(siteId, siteMembershipBodyCreate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(siteMembershipBodyCreate, 'siteMembershipBodyCreate');
        const pathParams = {
            siteId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/sites/{siteId}/group-members',
            pathParams,
            queryParams,
            bodyParam: siteMembershipBodyCreate,
            returnType: SiteGroupEntry
        });
    }
    listSiteGroups(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        const pathParams = {
            siteId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/sites/{siteId}/group-members',
            pathParams,
            queryParams,
            returnType: SiteGroupPaging
        });
    }
    getSiteGroupMembership(siteId, groupId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            siteId,
            groupId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/sites/{siteId}/group-members/{groupId}',
            pathParams,
            queryParams,
            returnType: SiteGroupEntry
        });
    }
    updateSiteGroupMembership(siteId, groupId, siteMembershipBodyUpdate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(siteMembershipBodyUpdate, 'siteMembershipBodyUpdate');
        const pathParams = {
            siteId,
            groupId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/sites/{siteId}/group-members/{groupId}',
            pathParams,
            queryParams,
            bodyParam: siteMembershipBodyUpdate,
            returnType: SiteGroupEntry
        });
    }
    deleteSiteGroupMembership(siteId, groupId) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            siteId,
            groupId
        };
        return this.delete({
            path: '/sites/{siteId}/group-members/{groupId}',
            pathParams
        });
    }
}
//# sourceMappingURL=../../../../../../../../lib/js-api/src/api/content-rest-api/api/sites.api.js.map